import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Alert,
  Stack,
  Skeleton,
  CircularProgress,
  Link,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import { BRAND, BRAND_DARKER, BG, ORG_SWAP_ANIMATION } from './healthWalletConstants';
import { ProviderOrgRow, ProviderOrgLists, orgKey, scrollOrgIntoView, deriveOrgOutcome, OrgOutcomeChip } from './ProviderOrgRow';
import { MissingProviderPicker } from './MissingProviderPicker';
import { ProviderRemoveButton } from './ProviderRemoveButton';
import {
  healthWalletApi,
  useGetOnboardingStatusQuery,
  usePatientDiscoveryMutation,
  useSkipOrgMutation,
  useRequestHealthDataMutation,
} from './healthWalletSlice';
import { useAppDispatch, useAuthProfile } from '../../app/hooks';
import type { EpicFhirOrg } from '../../types/onboarding';

/**
 * Onboarding "Find my providers" (the 'connect' step). Runs patient discovery ON ENTRY (if it hasn't run —
 * dataRequestId null), then surfaces ALL discovered orgs: Epic (FHIR REST/MyChart) with per-org Connect/Skip
 * (soft gate), and Kno2 (network) informationally ("we'll pull these automatically"). "Get my records" fires
 * the batched retrieval (requestHealthData with the discovery iasRequestId = dataRequestId) once no Epic org
 * still needs auth. Fully resumable from /onboarding/status — the Epic Connect is a full-page MyChart redirect
 * that reloads the SPA. See project_hw_unified_data_retrieval.
 */
// ORG_SWAP_ANIMATION moved to healthWalletConstants (2026-10-04) — the provider picker's connect
// offer uses the same slide, and two copies would drift. Behaviour here is unchanged.

export const OnboardingProviders: FC = () => {
  const dispatch = useAppDispatch();
  const { profile: loggedInUserProfile } = useAuthProfile();
  const { data: onboardingStatus } = useGetOnboardingStatusQuery();
  const [patientDiscovery, { isLoading: isDiscovering, isError: discoveryError }] = usePatientDiscoveryMutation();
  const [skipOrg] = useSkipOrgMutation();
  const [requestHealthData] = useRequestHealthDataMutation();

  // dataRequestId is the unified discovery/retrieval request id (sourced by /onboarding/status). null =
  // discovery hasn't run yet → run it on entry. It's also the iasRequestId "Get my records" triggers with.
  const discoveryRequestId = onboardingStatus?.dataRequestId ?? null;
  const epicOrgs = onboardingStatus?.epicFhirOrgs ?? [];
  const kno2Orgs = onboardingStatus?.kno2Orgs ?? [];

  // Auto-run discovery once on entry if none has run. Guarded against React double-invoke / re-renders.
  const discoveryStartedRef = useRef(false);
  useEffect(() => {
    if (discoveryRequestId) return; // already discovered (resume) → don't re-run
    if (discoveryStartedRef.current) return;
    discoveryStartedRef.current = true;
    (async () => {
      try {
        await patientDiscovery().unwrap(); // invalidates 'Onboarding' → status refetches with the orgs + dataRequestId
      } catch (e) {
        console.error('[OnboardingProviders] discovery failed', e);
        discoveryStartedRef.current = false; // allow a retry
      }
    })();
  }, [discoveryRequestId, patientDiscovery]);

  // Manual retry after a discovery failure — the auto-run effect above won't re-fire (its deps don't
  // change on failure). Re-triggering the mutation flips isLoading (which clears discoveryError), so the
  // spinner shows again and the outcome re-renders.
  const handleRetryDiscovery = () => { void patientDiscovery(); };

  const needsAuthCount = epicOrgs.filter((o) => o.status === 'needs_auth').length;
  const canProceed = !!discoveryRequestId && needsAuthCount === 0;
  // A failure clears the spinner (discoveryError term) so the error+retry branch renders instead of
  // spinning forever — !discoveryRequestId alone stays true on failure (id is only set on success).
  const showDiscovering = (isDiscovering || !discoveryRequestId) && !discoveryError;
  const totalOrgs = epicOrgs.length + kno2Orgs.length;

  // Guided connect wizard: walk through the Epic orgs that still need auth, ONE at a time.
  //   currentOrg = the next org needing auth (null = all connected/skipped → done).
  //   resolvedCount = how many are already connected/skipped (drives the "Provider X of Y" position).
  //   started gates intro → loop; session-persisted so a MyChart redirect+reload resumes mid-loop, and it's
  //   implied once any org has been acted on (resolvedCount > 0).
  const needsAuthOrgs = epicOrgs.filter((o) => o.status === 'needs_auth');
  const resolvedCount = epicOrgs.filter((o) => o.status === 'authed' || o.status === 'skipped').length;
  const currentOrg = needsAuthOrgs[0] ?? null;
  // `started` gates intro → loop. IN-MEMORY ONLY — no local/sessionStorage; we persist state via the backend.
  // It's OR'd with `resolvedCount > 0` (backend truth: any org authed/skipped), so a hard reload AFTER connecting
  // an org resumes in the loop. A reload BEFORE resolving any org harmlessly re-shows the intro (explanatory
  // screen + Continue — no state lost). See feedback_no_client_storage_persist_via_backend.
  const [startedConnect, setStartedConnect] = useState(false);
  const started = startedConnect || resolvedCount > 0;
  const beginConnecting = () => setStartedConnect(true);

  // "See all providers" shortcut: shows the full list (per-org Log in/Skip on every row) instead of the guided
  // one-at-a-time loop. Defaults to GUIDED (false) and is NOT persisted — the wizard always OPENS in guided mode
  // (Leo). (A MyChart redirect+reload from See-all lands back in the guided loop at the next org, which is fine.)
  const [viewAll, setViewAll] = useState(false);

  // Row to flash when the picker reports a provider was ALREADY on the list — cleared on a timer so the
  // highlight reads as "here it is" rather than a permanent selection. Only used on the review list;
  // a newly ADDED org needs none, since the screen flips to the guided step showing that org alone.
  const [highlightKey, setHighlightKey] = useState<string | null>(null);
  useEffect(() => {
    if (!highlightKey) return;
    const t = setTimeout(() => setHighlightKey(null), 4000);
    return () => clearTimeout(t);
  }, [highlightKey]);

  // Full-page form-POST → /onboarding/verify (Epic branch) → MyChart. deferRetrieval:true so the exchange
  // STORES the verification but skips per-org retrieval (batched later via "Get my records"). Mirrors the
  // Settings per-org Connect, + the deferRetrieval flag.
  const handleConnect = (org: EpicFhirOrg) => {
    if (!loggedInUserProfile?.id) {
      console.error('[OnboardingProviders] no profile id for Epic connect');
      return;
    }
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/api/healthwallet/onboarding/verify';
    form.style.display = 'none';
    const add = (name: string, value: string) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    };
    add('id', String(loggedInUserProfile.id));
    add('type', 'epic');
    add('iasRequestId', String(org.requestId ?? ''));
    add('orgIndex', String(org.index));
    add('deferRetrieval', 'true');
    document.body.appendChild(form);
    form.submit(); // full-page redirect to Epic MyChart; SPA reloads on return
    document.body.removeChild(form);
  };

  const handleSkip = (org: EpicFhirOrg) => {
    if (!org.requestId) return;
    // Optimistically flip this org to 'skipped' so the loop advances to the next needs_auth org INSTANTLY (no
    // refetch — skipOrg no longer invalidates 'Onboarding'). The server persists the flag, so a reload agrees.
    // Roll back on failure.
    const patch = dispatch(
      healthWalletApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
        const target = draft?.epicFhirOrgs?.find((o) => o.requestId === org.requestId && o.index === org.index);
        if (target) target.status = 'skipped';
      })
    );
    skipOrg({ iasRequestId: org.requestId, orgIndex: org.index }).unwrap().catch((e) => {
      console.error('[OnboardingProviders] skip failed', e);
      patch.undo();
    });
  };

  // A provider the PATIENT named via the "missing provider?" picker. The server has already appended it
  // to the discovery orgs and returned the row in epic_fhir_orgs shape, so we append THAT verbatim —
  // not a locally-assembled copy, which is how a parallel shape drifts out of step with the server's.
  // It lands as needs_auth, so it joins the connect loop like any discovered org.
  //
  // ★ The screen then flips to the guided connect/skip step for THAT org on its own — no routing here.
  // `currentOrg` is DERIVED as needsAuthOrgs[0], so one new needs_auth org ends the completion branch
  // (`currentOrg === null`) and the loop re-enters. ⛔ It re-opens ONLY the added org: orgs already
  // authed/skipped are not needs_auth and are untouched, and once this one is resolved currentOrg goes
  // back to null and we return to review. (Contrast handleStartOver, which deliberately un-skips ALL.)
  // Clearing viewAll only picks WHICH of the two post-review layouts shows — the guided step rather than
  // the expanded list — and changes no org's status.
  const handleProviderAdded = (org: EpicFhirOrg) => {
    dispatch(
      healthWalletApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
        if (!draft) return;
        if (!draft.epicFhirOrgs) draft.epicFhirOrgs = [];
        // Guard against a double-add racing the refetch; the server already deduped by FHIR URL.
        if (!draft.epicFhirOrgs.some((o) => o.requestId === org.requestId && o.index === org.index)) {
          draft.epicFhirOrgs.push(org);
        }
      })
    );
    setViewAll(false);
  };

  // The picked org was already on the list. Nothing was written, so the only useful response is to SHOW
  // them the row they already have — otherwise "already in your list" refers to something that may be
  // off-screen, which reads as nothing having happened. Highlight it for a few seconds as well as
  // scrolling, since a long list gives no other clue which row was meant.
  const handleProviderAlreadyPresent = (orgIndex: number) => {
    const existing = epicOrgs.find((o) => o.index === orgIndex);
    if (!existing) return;
    const key = orgKey(existing);
    setHighlightKey(key);
    scrollOrgIntoView(key);
  };

  // "Start over" (review step): UN-skip every skipped org so the guided loop re-enters at the first one.
  // The loop has no cursor — currentOrg is DERIVED (first needs_auth org), and skipped = "decided, never
  // revisit", so clearing the flags is the only way back in. Authed orgs stay connected. Optimistic flip
  // (instant re-entry) → server persists via skip-org {skipped:false} → revert on failure.
  const handleStartOver = () => {
    const skippedOrgs = epicOrgs.filter((o) => o.status === 'skipped' && o.requestId);
    if (!skippedOrgs.length) return;
    const patch = dispatch(
      healthWalletApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
        draft?.epicFhirOrgs?.forEach((o) => { if (o.status === 'skipped') o.status = 'needs_auth'; });
      })
    );
    setViewAll(false); // land in the guided one-at-a-time view, not the expanded list
    Promise.all(
      skippedOrgs.map((org) =>
        skipOrg({ iasRequestId: org.requestId!, orgIndex: org.index, skipped: false }).unwrap()
      )
    ).catch((e) => {
      console.error('[OnboardingProviders] start-over (un-skip) failed', e);
      patch.undo();
    });
  };

  // Per-org action buttons for the "See all" list (each row acts independently): authed → none; skipped →
  // "Log in" (reconsider/connect); needs_auth → "Log in" + "Skip". Same handlers as the guided loop.
  const actionsForEpic = (org: EpicFhirOrg): ReactNode => {
    // Delete a provider the PATIENT added by mistake. Self-hides unless this is their own pick with
    // nothing retrieved, so discovered orgs are unaffected. Leftmost — destructive actions sit away
    // from the primary CTA, which stays rightmost. Shown on BOTH lists that use this renderer (the
    // review list and "See all providers"), since the renderer is shared.
    const remove = <ProviderRemoveButton org={org} />;
    // An AUTHED org has no connect/skip action — but a pick that connected and pulled nothing is still
    // a mistake worth undoing, so this returns the remove button instead of the previous bare null.
    if (org.status === 'authed') return remove;
    if (org.status === 'skipped') {
      return (
        // OUTLINED pill (Leo 2026-07-14): in-row Log in buttons carry a border only — the screen's
        // ONE filled button is the main CTA (Get my records / guided Log in).
        <Stack direction="row" spacing={1} alignItems="center">
          {remove}
          <Button size="small" variant="outlined" onClick={() => handleConnect(org)}
            sx={{ color: BRAND_DARKER, borderColor: '#d7d3f0', textTransform: 'none', borderRadius: 50, '&:hover': { borderColor: BRAND_DARKER, bgcolor: 'transparent' } }}>
            Log in
          </Button>
        </Stack>
      );
    }
    return (
      // Skip left, Log in RIGHT (primary CTA rightmost — same convention as the guided loop's bottom row).
      <Stack direction="row" spacing={1} alignItems="center">
        {remove}
        <Button size="small" onClick={() => handleSkip(org)} sx={{ textTransform: 'none', color: BRAND_DARKER }}>
          Skip
        </Button>
        <Button size="small" variant="outlined" onClick={() => handleConnect(org)}
          sx={{ color: BRAND_DARKER, borderColor: '#d7d3f0', textTransform: 'none', borderRadius: 50, '&:hover': { borderColor: BRAND_DARKER, bgcolor: 'transparent' } }}>
          Log in
        </Button>
      </Stack>
    );
  };

  const handleGetRecords = () => {
    if (!discoveryRequestId) return;
    // Optimistic 'pending' routes the step machine connect→waiting; isOnboarded stays false so we land
    // on WalletLoadingScreen, not records. Reverted if the trigger fails (returns to connect).
    const patch = dispatch(
      healthWalletApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
        if (draft) {
          draft.dataRequestStatus = 'pending';
          draft.dataRequestType = 'ias';
        }
      })
    );
    // Fire the batched retrieval HERE (moved from WalletLoadingScreen's mount effect 2026-07-12, Leo:
    // the mount-fire re-triggered on every reload/remount of the waiting screen; a button fires exactly
    // once). Merge ONLY the request fields from the result — NOT the full state (isOnboarded would swap
    // straight to records — the 2026-07-09 bug). See project_hw_unified_data_retrieval.
    requestHealthData({ iasRequestId: discoveryRequestId }).unwrap()
      .then((result) => {
        if (result?.state) {
          dispatch(
            healthWalletApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
              if (draft) {
                draft.dataRequestId = result.state.dataRequestId;
                draft.dataRequestStatus = result.state.dataRequestStatus;
              }
            })
          );
        }
      })
      .catch((e) => {
        console.error('[OnboardingProviders] retrieval trigger failed', e);
        patch.undo();
      });
  };

  // The "Get my records" CTA — shown on the completion screen (all providers connected/skipped) and the
  // no-providers-found screen. Fires the batched retrieval via handleGetRecords.
  const getRecordsButton = (
    <Button
      variant="contained"
      onClick={handleGetRecords}
      disabled={!canProceed}
      endIcon={<ArrowForwardIcon />}
      sx={{ px: 4, bgcolor: BRAND_DARKER, borderRadius: 50, textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#5a3fc0' } }}
    >
      Get my records
    </Button>
  );

  return (
    <Box sx={{ bgcolor: BG, minHeight: '100svh', width: '100%', display: 'flex', flexDirection: 'column', mt: { xs: '-56px', sm: '-64px' } }}>
      {/* Header (flat — design cue: Overview) */}
      <Box
        component="header"
        sx={{
          pt: { xs: '88px', sm: '112px' },
          pb: { xs: 4, md: 5 },
          width: '100%',
        }}
      >
        {/* Compact step header — SAME shape as the other onboarding steps (OnboardingConsent /
            Verification / TrustBrowser): inline icon circle + "Step N of M" overline + short title.
            ⛔ Replaced the old CENTRED hero (80px circle, big "Find my providers", one-line description),
            which repeated what the card below says better on every branch (Leo 2026-10-04). The title
            here is the STEP's name; the card keeps "Your providers" and the count. */}
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: BRAND, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)' }} aria-hidden="true">
              <SearchIcon sx={{ color: BRAND_DARKER, fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="overline" sx={{ color: '#8e8e93', letterSpacing: 1.5 }}>
                Step 4 of 4
              </Typography>
              <Typography variant="h5" fontWeight={700} sx={{ color: '#1c1c1e' }}>
                Find my providers
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container component="main" maxWidth="sm" sx={{ mt: -3, pb: 6, position: 'relative', zIndex: 1 }}>
        <Paper elevation={3} sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.08)' }}>
          <Box sx={{ p: { xs: 3, md: 4 } }}>
            {/* Purple-tone header icon — kept on the SPARSE screens (discovery / intro / none-found);
                hidden on completion AND the guided connect view (Leo 2026-07-14: "too much going on" —
                the decoration competed with the org row). */}
            {(showDiscovering || totalOrgs === 0 || (currentOrg !== null && !started && !viewAll)) ? (
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    bgcolor: '#efeaff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                  }}
                >
                  <LocalHospitalIcon sx={{ fontSize: 32, color: BRAND_DARKER }} />
                </Box>
              </Box>
            ) : null}
            {showDiscovering ? (
              /* Discovery in flight — prominent "getting your providers" status + spinner + skeleton org rows +
                 a disabled Continue, so the screen isn't empty while we find providers (Leo). */
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 2 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#1c1c1e', mb: 1.5 }}>
                    Your providers
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: BRAND_DARKER, mb: 0.5 }}>
                    Getting your list of providers…
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    We'll then walk you through connecting to each one, one at a time.
                  </Typography>
                </Box>
                {/* Skeleton rows with the big spinner centered OVER them (Leo). */}
                <Box sx={{ position: 'relative' }}>
                  <Stack spacing={1.5}>
                    {[0, 1, 2].map((i) => (
                      <Skeleton key={i} variant="rounded" height={72} sx={{ borderRadius: 2 }} />
                    ))}
                  </Stack>
                  <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress sx={{ color: BRAND_DARKER }} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    disabled
                    endIcon={<ArrowForwardIcon />}
                    sx={{ px: 4, bgcolor: BRAND_DARKER, borderRadius: 50, textTransform: 'none', fontWeight: 700 }}
                  >
                    Continue
                  </Button>
                </Box>
              </Box>
            ) : discoveryError ? (
              /* Discovery FAILED — surface it with a retry instead of an infinite spinner, and do NOT
                 fall through to the "no providers found → continue" path below (discovery didn't
                 complete, so we don't actually know whether there are providers). */
              <Box sx={{ py: 2 }}>
                <Alert severity="error" sx={{ borderRadius: 2, mb: 3 }}>
                  We couldn't search for your providers right now. Please try again.
                </Alert>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={handleRetryDiscovery}
                    endIcon={<ArrowForwardIcon />}
                    sx={{ px: 4, bgcolor: BRAND_DARKER, borderRadius: 50, textTransform: 'none', fontWeight: 700 }}
                  >
                    Try again
                  </Button>
                </Box>
              </Box>
            ) : (
              <>

                {totalOrgs === 0 ? (
                  /* No providers found — nothing to connect; let the user continue. */
                  <Box sx={{ py: 2 }}>
                    <Alert severity="info" sx={{ borderRadius: 2, mb: 3 }}>
                      We didn't find any organizations with records for you in the network yet. You can still
                      continue — we'll keep looking, and you can add records anytime.
                    </Alert>

                    {/* ★ The mount that matters MOST (Leo 2026-10-03): a patient who matched nothing is
                        exactly who the picker was built for, and without it this screen is a dead end —
                        "we found nothing" with no way to say "but I go to Yale". Adding here moves them
                        to the intro with their one org listed, via the same derived branching. */}
                    <MissingProviderPicker
                      onAdded={handleProviderAdded}
                      onAlreadyPresent={handleProviderAlreadyPresent}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                      {getRecordsButton}
                    </Box>
                  </Box>
                ) : currentOrg === null ? (
                  /* COMPLETION / REVIEW — every provider is connected or skipped; the screen flips here on its
                     own. Shows all orgs for review (skipped ones keep a Log in to reconsider) + Get my records.
                     Slides in like the per-org steps (mounts fresh when the branch flips → animation plays). */
                  <Box sx={ORG_SWAP_ANIMATION}>
                    {/* Compact confirmation line (Leo 2026-07-14: big check + title + body was too much) —
                        the LIST is the subject; the skipped rows' outlined Log in already says
                        "you can still connect". */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2.5 }}>
                      <CheckCircleIcon sx={{ fontSize: 22, color: '#16a34a' }} />
                      <Typography variant="h6" fontWeight={700} sx={{ color: '#1c1c1e' }}>
                        All set — {epicOrgs.filter((o) => o.status === 'authed').length} connected
                        {epicOrgs.some((o) => o.status === 'skipped') ? ` · ${epicOrgs.filter((o) => o.status === 'skipped').length} skipped` : ''}
                      </Typography>
                    </Box>

                    <ProviderOrgLists
                      epicOrgs={epicOrgs}
                      kno2Orgs={kno2Orgs}
                      selectedKey={highlightKey}
                      renderEpicTrailing={(org) => actionsForEpic(org)}
                    />

                    {/* Placed at the END of the connect loop deliberately: the patient has now SEEN what
                        we found, which is the moment "but what about X?" occurs to them. Collapsed, so it
                        never competes with the providers we did find.
                        ⛔ No onConnectNow here — in onboarding the screen flips to this org's guided
                        connect/skip step by itself, which keeps Skip reachable and the batch intact. */}
                    <MissingProviderPicker
                      onAdded={handleProviderAdded}
                      onAlreadyPresent={handleProviderAlreadyPresent}
                    />

                    {/* Grey footer band (full-bleed against the card's padding) — actions live here,
                        off the white stage. "Start over" re-opens all SKIPPED orgs; hidden when none. */}
                    <Box sx={{ mx: { xs: -3, md: -4 }, mb: { xs: -3, md: -4 }, mt: 3, px: { xs: 3, md: 4 }, py: 2.5, bgcolor: '#f7f7fa', borderTop: '1px solid #ececf1' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: epicOrgs.some((o) => o.status === 'skipped') ? 'space-between' : 'flex-end' }}>
                        {epicOrgs.some((o) => o.status === 'skipped') ? (
                          <Button onClick={handleStartOver} sx={{ textTransform: 'none', color: BRAND_DARKER, fontSize: '0.8rem' }}>
                            Start over
                          </Button>
                        ) : null}
                        {getRecordsButton}
                      </Box>
                    </Box>
                  </Box>
                ) : !started && !viewAll ? (
                  /* INTRO — flex-COLUMN with `gap` (no per-element margins) so the spacing above AND below the
                     org list is uniform + deterministic (Leo). */
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, py: 2 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" fontWeight={700} sx={{ color: '#1c1c1e', mb: 1 }}>
                        Your providers
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        We found {epicOrgs.length} provider portal{epicOrgs.length === 1 ? '' : 's'} with your records.
                        We'll walk you through them one at a time — sign in to each, or skip any you don't want now.
                        {kno2Orgs.length > 0 ? ` ${kno2Orgs.length} more will be pulled automatically.` : ''}
                      </Typography>
                    </Box>

                    {/* Preview the discovered providers (display only) — Continue starts the GUIDED connect. */}
                    <ProviderOrgLists epicOrgs={epicOrgs} kno2Orgs={kno2Orgs} selectedKey={highlightKey} />

                    {/* Also offered here, not just after the loop (Leo 2026-10-03): a patient who can see
                        the whole list up front may spot the omission straight away. Adding here does NOT
                        jump them into the guided loop — `started` is still false, so this branch holds and
                        the new org simply joins the list until they press Continue. */}
                    <MissingProviderPicker
                      onAdded={handleProviderAdded}
                      onAlreadyPresent={handleProviderAlreadyPresent}
                    />

                    {/* Bottom action row — Continue right-justified. "See all providers" is intentionally NOT
                        here; it's only relevant AFTER Continue (in the guided loop). */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        onClick={beginConnecting}
                        endIcon={<ArrowForwardIcon />}
                        sx={{ px: 4, bgcolor: BRAND_DARKER, borderRadius: 50, textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#5a3fc0' } }}
                      >
                        Continue
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  /* CONNECTING — the guided Log in/Skip for the current org are ALWAYS shown (Leo); "See all
                     providers" INDEPENDENTLY expands the full list below (per-org actions, current highlighted)
                     and toggles back. ZONED CARD (Leo 2026-07-14, "too much going on"): the white stage holds
                     only the eyebrow + progress strip + the org row (THE subject); reassurance, account help
                     and every button live in a grey full-bleed footer band. Decorative icon dropped here. */
                  <Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, color: BRAND_DARKER, textTransform: 'uppercase', textAlign: 'center', mb: 1.25 }}>
                      Connect your providers
                    </Typography>
                    {/* Slim per-provider progress strip — same info as the old bold "Provider X of Y" line,
                        quieter: one segment per org (accent = handled, tint = current, hairline = ahead). */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25, mb: 2.5 }}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {epicOrgs.map((o) => (
                          <Box key={orgKey(o)} sx={{
                            width: { xs: 14, sm: 22 }, height: 4, borderRadius: 2,
                            bgcolor: (o.status === 'authed' || o.status === 'skipped') ? BRAND_DARKER
                              : orgKey(o) === orgKey(currentOrg) ? '#d7d3f0' : '#ececf1',
                          }} />
                        ))}
                      </Box>
                      <Typography sx={{ fontSize: 12, color: '#8e8e93', whiteSpace: 'nowrap' }}>
                        Provider {resolvedCount + 1} of {epicOrgs.length}
                      </Typography>
                    </Box>

                    {viewAll ? (
                      <ProviderOrgLists
                        epicOrgs={epicOrgs}
                        kno2Orgs={kno2Orgs}
                        selectedKey={orgKey(currentOrg)}
                        renderEpicTrailing={(org) => actionsForEpic(org)}
                      />
                    ) : (
                      <Box key={orgKey(currentOrg)} sx={ORG_SWAP_ANIMATION}>
                        <ProviderOrgRow
                          displayName={currentOrg.displayName}
                          address={currentOrg.address}
                          selected
                          statusChip={<OrgOutcomeChip outcome={deriveOrgOutcome(currentOrg)} reason={currentOrg.lastError} />}
                        />
                      </Box>
                    )}

                    {/* Grey footer band (full-bleed against the card's p:{3,4}) — the "supporting furniture":
                        one-line reassurance (the old tinted info box, detinted), account help, actions.
                        Skip-is-safe copy now lives ON the button ("Skip for now"). */}
                    <Box sx={{ mx: { xs: -3, md: -4 }, mb: { xs: -3, md: -4 }, mt: 3, px: { xs: 3, md: 4 }, py: 2.5, bgcolor: '#f7f7fa', borderTop: '1px solid #ececf1' }}>
                      <Typography variant="body2" sx={{ color: '#6b6b70', textAlign: 'center' }}>
                        {viewAll
                          ? 'Sign in to the highlighted provider — or connect or skip any provider in the list.'
                          : <>You&rsquo;ll sign in on your provider&rsquo;s secure site — <Box component="span" sx={{ fontWeight: 700, color: '#3a3a3c' }}>we never see your password</Box>.</>}
                      </Typography>
                      {/* MyChart account help for the CURRENT org (guided mode) — opens the org's
                          MyChart sign-in/sign-up in a new tab. See EpicFhirOrg.myChartUrl. */}
                      {!viewAll && currentOrg.myChartUrl ? (
                        <Typography variant="body2" sx={{ mt: 0.5, textAlign: 'center', color: '#6b6b70' }}>
                          {'No account or forgot your password? '}
                          <Link href={currentOrg.myChartUrl} target="_blank" rel="noopener noreferrer" sx={{ color: BRAND_DARKER, fontWeight: 700 }}>
                            Click here
                          </Link>
                        </Typography>
                      ) : null}

                      {/* Action row — CTA(s) right-justified, See-all/guided toggle on the left (Leo).
                          On PHONES (xs) all three don't fit one line → column: toggle on its own line,
                          left-justified (negative ml offsets the text-button padding), CTAs keep right. */}
                      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: { xs: 2, sm: 0 }, mt: 2 }}>
                        {/* LEFT SLOT — "Start over" took this position from "See all providers" (Leo 2026-10-04):
                            reconsidering a skip is the thing a patient wants mid-loop, and it was previously
                            only reachable at the END, by which point they had already finished the flow
                            they wanted to redo. "See all" drops to the quieter line beneath it.
                            Hidden with nothing skipped — it un-skips, so with no skips there is nothing to
                            undo (same rule as the review screen). */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'flex-start' } }}>
                          {epicOrgs.some((o) => o.status === 'skipped') ? (
                            <Button
                              onClick={handleStartOver}
                              sx={{ textTransform: 'none', color: BRAND_DARKER, fontSize: '0.8rem', alignSelf: { xs: 'flex-start', sm: 'auto' }, ml: { xs: -1, sm: 0 } }}
                            >
                              Start over
                            </Button>
                          ) : null}
                          {/* Demoted: smaller, muted, underlined — a secondary way to look around rather
                              than a peer of the primary action. */}
                          <Link
                            component="button"
                            type="button"
                            onClick={() => { if (viewAll) { setViewAll(false); beginConnecting(); } else { setViewAll(true); } }}
                            sx={{ textTransform: 'none', color: '#6b6b70', fontSize: '0.75rem', textAlign: 'left', alignSelf: { xs: 'flex-start', sm: 'auto' } }}
                          >
                            {viewAll ? 'Back to guided setup' : 'See all providers'}
                          </Link>
                        </Box>
                        <Stack key={orgKey(currentOrg)} direction="row" spacing={1.5} sx={{ alignSelf: { xs: 'flex-end', sm: 'auto' }, ...ORG_SWAP_ANIMATION }}>
                          <Button
                            variant="outlined"
                            onClick={() => handleSkip(currentOrg)}
                            sx={{ px: 3, borderRadius: 50, textTransform: 'none', fontWeight: 600, color: BRAND_DARKER, borderColor: '#d7d3f0', '&:hover': { borderColor: BRAND_DARKER, bgcolor: 'transparent' } }}
                          >
                            Skip for now
                          </Button>
                          <Button
                            variant="contained"
                            onClick={() => handleConnect(currentOrg)}
                            sx={{ px: 4, bgcolor: BRAND_DARKER, borderRadius: 50, textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#5a3fc0' } }}
                          >
                            Log in
                          </Button>
                        </Stack>
                      </Box>
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
